import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { UUID } from '@/types';
import ColumnName from '@/util/ColumnName';
import { csrfToken } from '@/util/helpers/ajax-helpers';
import { TSelectedMetricsDimensions } from '@/features/chart-configuration/apiSlice';

export const GET_DASHBOARD_FILTERS_CONFIGURATION =
  'getDashboardFiltersConfiguration';

export const GET_SAVED_QUERY_SAVED_AGGREGATE_CONFIG =
  'getSavedQuerySavedAggregateConfig';

export const UPDATE_DASHBOARD_FILTERS = 'updateDashboardFilters';

export const GET_EMBEDDED_CONFIG = 'getEmbeddedConfig';
export const SAVE_EMBEDDED_CONFIG = 'saveEmbeddedConfig';
export const REGENERATE_API_KEY = 'regenerateApiKey';

const API_KEY = 'api-key';
export const UPDATE_PUBLIC_PERMISSION = 'updatePublicPermission';

export type TFilterConfigurationInput = {
  datasourceId: UUID;
  dashboardId: UUID;
};

export type TFiltersUpdateInput = TFilterConfigurationInput & {
  body: {
    seriesId: UUID;
    dashboardFiltersIds: UUID[];
    filtersIdsToRemove: UUID[];
    matchingColumns: ColumnName[];
  };
};

export type TFilterConfiguration = {
  filterId: UUID;
  columnsBySeries: Record<UUID, { _alias: string; _name: string }[]>;
};

export type TFiltersUpdateResponse = TFilterConfiguration[];

export type TGetEmbeddedConfigInput = {
  datasourceId: UUID;
};

export type TGetEmbeddedConfigResponse = {
  apiKey: UUID;
  redirectUrl: Maybe<string>;
};

export type TSaveEmbeddedConfigInput = {
  datasourceId: UUID;
  body: {
    redirectUrl: Maybe<string>;
  };
};

export type TRegenerateApiInput = {
  datasourceId: UUID;
};

export type TRegenerateApiResponse = TGetEmbeddedConfigResponse;

export type TUpdatePublicPermissionInput = {
  datasourceId: UUID;
  dashboardId: UUID;
  body: {
    isPublic: boolean;
  };
};

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  tagTypes: [API_KEY],
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/datasources/',
    credentials: 'same-origin',
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json;charset=UTF-8');
      headers.set('x-requested-with', 'XMLHttpRequest');

      const token = csrfToken();
      if (token) headers.set('x-csrf-token', token);

      return headers;
    },
  }),
  endpoints: (builder) => ({
    [GET_DASHBOARD_FILTERS_CONFIGURATION]: builder.query<
      TFilterConfiguration[],
      TFilterConfigurationInput
    >({
      query: ({ datasourceId, dashboardId }) =>
        `${datasourceId}/dashboards/${dashboardId}/filters-configuration`,
    }),
    [GET_SAVED_QUERY_SAVED_AGGREGATE_CONFIG]: builder.query<
      Record<string, TSelectedMetricsDimensions>,
      { datasourceId: UUID; seriesId?: UUID }
    >({
      query: ({ datasourceId, seriesId }) =>
        `${datasourceId}/saved-queries/aggregate-configuration/${seriesId}`,
    }),
    [UPDATE_DASHBOARD_FILTERS]: builder.mutation<
      TFiltersUpdateResponse,
      TFiltersUpdateInput
    >({
      query: ({ datasourceId, dashboardId, body }) => ({
        method: 'PUT',
        url: `${datasourceId}/dashboards/${dashboardId}/filters`,
        body,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        // TODO: remove after LEGACY state refactoring.
        dispatch({
          type: 'UPDATE_DASHBOARD_FILTERS',
          payload: data,
        });
      },
    }),
    [GET_EMBEDDED_CONFIG]: builder.query<
      TGetEmbeddedConfigResponse,
      TGetEmbeddedConfigInput
    >({
      providesTags: [API_KEY],
      query: ({ datasourceId }) =>
        `${datasourceId}/dashboards/embedded-configuration`,
    }),
    [SAVE_EMBEDDED_CONFIG]: builder.mutation<null, TSaveEmbeddedConfigInput>({
      invalidatesTags: [API_KEY],

      query: ({ datasourceId, body }) => ({
        method: 'POST',
        url: `${datasourceId}/dashboards/save-embedded-configuration`,
        body,
      }),
    }),
    [REGENERATE_API_KEY]: builder.mutation<
      TRegenerateApiResponse,
      TRegenerateApiInput
    >({
      invalidatesTags: [API_KEY],
      query: ({ datasourceId }) => ({
        method: 'POST',
        url: `${datasourceId}/dashboards/regenerate-api-key`,
      }),
    }),

    [UPDATE_PUBLIC_PERMISSION]: builder.mutation<
      undefined,
      TUpdatePublicPermissionInput
    >({
      query: ({ datasourceId, dashboardId, body }) => ({
        method: 'POST',
        url: `${datasourceId}/dashboards/${dashboardId}/public-toggle`,
        body,
      }),
    }),
  }),
});

export const {
  useGetDashboardFiltersConfigurationQuery,
  useGetSavedQuerySavedAggregateConfigQuery,
  useUpdateDashboardFiltersMutation,
  useGetEmbeddedConfigQuery,
  useSaveEmbeddedConfigMutation,
  useRegenerateApiKeyMutation,
  useUpdatePublicPermissionMutation,
} = dashboardApi;
